import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { photoUrl } from "@/lib/utils-app";
import { PawPrintIcon as PawPrint, HeartIcon as Heart, TrophyIcon as Trophy, SmileyIcon as Smiley, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const ICONS = { paw: PawPrint, love: Heart, champion: Trophy, laugh: Smiley };

export default function PublicPost() {
  const { postId } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(undefined); // undefined=loading, null=missing

  useEffect(() => {
    axios
      .get(`${API}/posts/public/${postId}`)
      .then((r) => {
        setPost(r.data);
        if (r.data?.dog?.name) document.title = `${r.data.dog.name} on The Dog's Voice — Armstrong K9 Registry`;
      })
      .catch(() => setPost(null));
  }, [postId]);

  if (post === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">Loading…</div>;
  }
  if (post === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <PawPrint size={40} weight="duotone" className="text-[#C9A227]" />
        <p className="font-spectral text-2xl text-ink">This post has wandered off.</p>
        <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
          <Link to="/">Visit Armstrong K9 Registry</Link>
        </Button>
      </div>
    );
  }

  const counts = post.reaction_counts || {};
  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="public-post-page">
      <header className="bg-[#0E0E0E] text-white px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-spectral text-lg tracking-tight">Armstrong K9 Registry</Link>
        {!user && (
          <Button asChild size="sm" className="rounded-full bg-white text-ink hover:bg-[#C9A227]">
            <Link to="/register" data-testid="public-post-join-cta">Join free</Link>
          </Button>
        )}
      </header>
      <main className="max-w-xl mx-auto px-4 py-8">
        <article className="rounded-2xl bg-white border border-black/10 overflow-hidden shadow-sm">
          <div className="p-5 flex items-center gap-3">
            {post.dog?.photo ? (
              <img src={photoUrl(post.dog.photo)} alt={post.dog?.name} className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-[#F2F2F2] flex items-center justify-center">
                <PawPrint size={20} weight="duotone" className="text-[#6B6B6B]" />
              </div>
            )}
            <div>
              <p className="font-bold text-ink leading-tight">{post.dog?.name}</p>
              <p className="text-[12px] text-[#6B6B6B]">
                {post.dog?.breed}{post.author_name ? ` · ${post.author_name}` : ""}
              </p>
            </div>
          </div>
          {post.text && <p className="px-5 pb-4 text-[15px] text-ink whitespace-pre-wrap">{post.text}</p>}
          {post.video_path ? (
            <video src={photoUrl(post.video_path)} poster={post.video_poster ? photoUrl(post.video_poster) : undefined} className="w-full bg-black" controls playsInline muted autoPlay loop />
          ) : (
            (post.photos || []).map((p, i) => (
              <img key={i} src={photoUrl(p)} alt="" className="w-full object-cover" data-testid={`public-post-photo-${i}`} />
            ))
          )}
          <div className="p-4 flex items-center gap-3 border-t border-[#EFEFEF]">
            {Object.entries(ICONS).map(([key, Icon]) =>
              counts[key] ? (
                <span key={key} className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B]">
                  <Icon size={15} weight="fill" className="text-[#C1272D]" />
                  {counts[key]}
                </span>
              ) : null
            )}
            {post.dog?.for_sale && (
              <Link to={`/dogs/${post.dog.id}`} className="ml-auto text-[12.5px] font-bold text-[#0E6B3A]" data-testid="public-post-inquire-link">
                Available — Inquire
              </Link>
            )}
          </div>
        </article>

        <div className="mt-8 rounded-2xl bg-[#0E0E0E] text-white p-6 text-center">
          <p className="font-spectral text-xl mb-1">The Dog's Voice</p>
          <p className="text-[13px] text-white/70 mb-4">
            Posts written from the dogs' perspective — on Armstrong K9 Registry, where every dog has papers and a page.
          </p>
          <Button asChild className="rounded-full bg-white text-ink hover:bg-[#C9A227]">
            <Link to={user ? "/feed" : "/register"} data-testid="public-post-bottom-cta">
              {user ? "Open the feed" : "Join free"} <ArrowRight size={14} className="ml-1.5" />
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
